import queryString from 'query-string';
import { apiUrl, authHeaders } from './auth';
import { IGrant } from '~/types/campaigns';

export type AppliedGrantsFilters = {
    ntaf_id?: string;
    name?: string;
    start_date?: string;
    end_date?: string;
    // Only the most recent N grants (the dashboard card)
    limit?: number;
};

export const fetchAppliedGrants = async (
    filters: AppliedGrantsFilters = {}
): Promise<IGrant[]> => {
    const headers = authHeaders();
    const search = queryString.stringify(filters, {
        skipNull: true,
        skipEmptyString: true,
    });
    const url =
        apiUrl + '/admin/applied-grants' + (search ? `?${search}` : '');
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    if (!resp.ok) {
        throw new Error('Failed to fetch applied grants');
    }
    return await resp.json();
};

export type GrantNameOption = { value: string; label: string };
export type GrantCampaignOption = { value: string; label: string };
export type GrantNtafOption = { value: string; label: string };

const fetchTypeaheadOptions = async <T>(
    path: string,
    query: string,
    extra: Record<string, string> = {}
): Promise<T[]> => {
    const headers = authHeaders();
    const search = queryString.stringify(
        { search: query, ...extra },
        { skipEmptyString: true }
    );
    const url = apiUrl + path + (search ? `?${search}` : '');
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    if (!resp.ok) {
        throw new Error(`Failed to search ${path}`);
    }
    return await resp.json();
};

export const searchGrantNames = (query: string) =>
    fetchTypeaheadOptions<GrantNameOption>('/admin/dropdown/grants', query);

export const searchGrantCampaigns = (
    query: string,
    options: { all?: boolean } = {}
) =>
    fetchTypeaheadOptions<GrantCampaignOption>(
        '/admin/dropdown/grant-campaigns',
        query,
        options.all ? { all: 'true' } : {}
    );

export const searchGrantNtafs = (
    query: string,
    options: { all?: boolean } = {}
) =>
    fetchTypeaheadOptions<GrantNtafOption>(
        '/admin/dropdown/grant-ntafs',
        query,
        options.all ? { all: 'true' } : {}
    );

export type CreateAppliedGrantPayload = {
    name: string;
    ntaf_id: string;
    amount: string;
    date: string;
};

export const createAppliedGrant = async (
    payload: CreateAppliedGrantPayload
): Promise<IGrant> => {
    const headers = {
        ...authHeaders(),
        'Content-Type': 'application/json',
    };
    const resp = await fetch(apiUrl + '/admin/applied-grants', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
    });
    if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || 'Failed to create grant');
    }
    return await resp.json();
};

export const updateAppliedGrant = async (
    id: number,
    payload: Partial<CreateAppliedGrantPayload>
): Promise<IGrant> => {
    const headers = {
        ...authHeaders(),
        'Content-Type': 'application/json',
    };
    const resp = await fetch(apiUrl + `/admin/applied-grants/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(payload),
    });
    if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || 'Failed to update grant');
    }
    return await resp.json();
};

export const deleteAppliedGrant = async (id: number): Promise<void> => {
    const headers = authHeaders();
    const resp = await fetch(apiUrl + `/admin/applied-grants/${id}`, {
        method: 'DELETE',
        headers,
    });
    if (!resp.ok) {
        const text = await resp.text();
        throw new Error(text || 'Failed to delete grant');
    }
};
